// Narration vocabulary for the guided tour. Every spoken sentence is a fixed clip rendered offline
// with a neural voice (tools/narration), so tours sound natural; details that change (times, exact
// numbers) are shown on screen instead of spoken.

import { DISTANCE_RANGE } from '../data/distances';

export interface Clip {
  key: string;
  text: string;
}

export const DIRECTIONS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];
/** The 28 lunar stations as spoken, in Ibn Qutaybah's order (pronunciations in tools/narration/lexicon.json). */
export const MANAZIL = ['al-Sharaṭān', 'al-Buṭayn', 'al-Thurayyā', 'al-Dabarān', 'al-Haqʿah', 'al-Hanʿah', 'al-Dhirāʿ', 'al-Nathrah', 'al-Ṭarf', 'al-Jabhah', 'al-Zubrah', 'al-Ṣarfah', 'al-ʿAwwāʾ', 'al-Simāk', 'al-Ghafr', 'al-Zubāná', 'al-Iklīl', 'al-Qalb', 'al-Shawlah', 'al-Naʿāʾim', 'al-Baldah', 'Saʿd al-Dhābiḥ', 'Saʿd Bulaʿ', 'Saʿd al-Suʿūd', 'Saʿd al-Akhbiyah', 'al-Fargh al-Muqaddam', 'al-Fargh al-Muʾakhkhar', 'al-Rishāʾ'];
const DIR_KEYS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];
const HEIGHTS: [string, string][] = [['low', 'low'], ['half', 'halfway up'], ['high', 'high']];

const GREEK: Record<string, string> = {
  α: 'Alpha', β: 'Beta', γ: 'Gamma', δ: 'Delta', ε: 'Epsilon', ζ: 'Zeta', η: 'Eta', θ: 'Theta', κ: 'Kappa', λ: 'Lambda', μ: 'Mu', π: 'Pi', σ: 'Sigma', χ: 'Chi', ο: 'Omicron',
};
/** Spell Greek letters for speech: "η-Aquariids" → "Eta Aquariids". */
export const speakable = (s: string) => s.replace(/([αβγδεζηθκλμπσχο])-/g, (_, g: string) => `${GREEK[g]} `);
const slug = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const clip = {
  intro: (): Clip => ({ key: 'intro', text: 'Welcome to tonight’s sky. Let’s look at the highlights. You can tap anything along the way to learn more.' }),
  jumpToEvening: (): Clip => ({ key: 'intro-evening', text: 'It’s still light, so let’s jump ahead to this evening.' }),
  outro: (): Clip => ({ key: 'outro', text: 'That’s the tour. Tap the phone button to point at the real sky, and tap anything you see to learn more. Clear skies!' }),
  location(alt: number, az: number): Clip {
    if (alt > 70) return { key: 'loc-overhead', text: 'Look almost straight up.' };
    const h = alt > 45 ? HEIGHTS[2] : alt > 20 ? HEIGHTS[1] : HEIGHTS[0];
    const k = Math.round((((az % 360) + 360) % 360) / 45) % 8;
    return { key: `loc-${h[0]}-${DIR_KEYS[k]}`, text: `Look ${h[1]} in the ${DIRECTIONS[k]}.` };
  },
  allLocations(): Clip[] {
    const out: Clip[] = [{ key: 'loc-overhead', text: 'Look almost straight up.' }];
    for (const [hk, hw] of HEIGHTS) DIRECTIONS.forEach((d, i) => out.push({ key: `loc-${hk}-${DIR_KEYS[i]}`, text: `Look ${hw} in the ${d}.` }));
    return out;
  },
  moon: (): Clip => ({ key: 'moon', text: 'First, the Moon.' }),
  moonPhase(phaseName: string): Clip {
    const t: Record<string, string> = {
      'Waxing crescent': 'Tonight it’s a thin waxing crescent, growing a little each night.',
      'First quarter': 'Tonight it’s at first quarter: half lit, and growing.',
      'Waxing gibbous': 'Tonight it’s a waxing gibbous, more than half lit and growing.',
      'Full Moon': 'Tonight the Moon is full.',
      'Waning gibbous': 'Tonight it’s a waning gibbous, rising a little later each night.',
      'Last quarter': 'Tonight it’s at last quarter, rising around midnight.',
      'Waning crescent': 'Tonight it’s a thin waning crescent, best seen before dawn.',
      'New Moon': 'Tonight it’s new Moon, so the sky is at its darkest.',
    };
    return { key: `moon-${slug(phaseName)}`, text: t[phaseName] ?? `Tonight it’s a ${phaseName.toLowerCase()}.` };
  },
  moonTip: (full: boolean): Clip =>
    full
      ? { key: 'moon-tip-full', text: 'At full Moon there are few shadows, but the bright rays spreading from the crater Tycho stand out beautifully in binoculars.' }
      : { key: 'moon-tip', text: 'Through binoculars, look along the line between light and dark, where long shadows make the craters stand out.' },
  planet: (id: string): Clip => ({ key: `planet-${slug(id)}`, text: `Next, the planet ${id}.` }),
  planetFact: (id: string, fact: string): Clip => ({ key: `planet-${slug(id)}-fact`, text: fact }),
  planetSteady: (): Clip => ({ key: 'planet-steady', text: 'Planets shine with a steady light; stars twinkle.' }),
  /** `meaning` is used as given (callers lower-case plain English names); '' for none. */
  constellation: (id: string, name: string, meaning: string): Clip => ({ key: `con-${slug(id)}`, text: meaning ? `High overhead is ${name}, the ${meaning}.` : `High overhead is ${name}.` }),
  /** With `arabic`, the star's Arabic name too (its own clip, for when Arabic is switched on). */
  constellationStar(id: string, star: string, arabic?: { tr: string; meaning: string; fromArabic?: false }): Clip {
    if (!arabic) return { key: `con-${slug(id)}-star`, text: `Its brightest star is ${star}.` };
    const ar =
      arabic.meaning && arabic.fromArabic !== false
        ? `, from the Arabic ${arabic.tr}, meaning ${arabic.meaning}.`
        : `, known in Arabic as ${arabic.tr}${arabic.meaning ? `, ${arabic.meaning}` : ''}.`;
    return { key: `con-${slug(id)}-star-ar`, text: `Its brightest star is ${star}${ar}` };
  },
  star: (name: string): Clip => ({ key: `star-${slug(name)}`, text: `The brightest star up now is ${name}.` }),
  /**
   * The star's distance, as the tour also shows it: a distance, never a time (owner's item 30), said as the star's
   * spoken facts say it (data-src/facts/facts.json). One decimal under 10 light years; under 20, a card value of x.5 as
   * "x and a half" (Procyon); two figures under 1,000, then to the nearest 10 as the card gives it. A distance still
   * disputed is the range the facts give.
   */
  starDistance(name: string, ly: number): Clip {
    const tenth = Math.round(ly * 10) / 10;
    const range = DISTANCE_RANGE[name];
    const n = range ? `somewhere between about ${range[0].toLocaleString('en-GB')} and ${range[1].toLocaleString('en-GB')}`
      : `about ${ly < 10 ? String(tenth) : ly < 20 && tenth % 1 === 0.5 ? `${Math.floor(tenth)} and a half` : (ly < 1000 ? Number(ly.toPrecision(2)) : Math.round(ly / 10) * 10).toLocaleString('en-GB')}`;
    return { key: `star-${slug(name)}-distance`, text: `It is ${n} light years away.` };
  },
  starArabic: (name: string, tr: string, meaning: string, fromArabic?: false): Clip => ({
    key: `star-${slug(name)}-ar`,
    text: meaning && fromArabic !== false ? `The name comes from the Arabic ${tr}, meaning ${meaning}.` : `In Arabic it is called ${tr}${meaning ? `, ${meaning}` : ''}.`,
  }),
  sirius: (): Clip => ({ key: 'star-sirius-quran', text: 'In Arabic it is al-Shiʿrá, the star named in the Quran, in Sūrat al-Najm.' }),
  /** The Moon's station (manzil) tonight, 1 to 28. */
  moonStation: (n: number): Clip => ({ key: `moon-station-${n}`, text: `In the Arabic tradition, tonight the Moon is in its station of ${MANAZIL[n - 1]}.` }),
  /** With `arabic`, the Pleiades clip also gives their Arabic name. */
  dso(id: string, arabic = false): Clip | null {
    if (id === 'M45' && arabic) return { key: 'dso-m45-ar', text: 'Now find the Pleiades, a young cluster of blue stars, known in Arabic as al-Thurayyā. Binoculars show dozens of them.' };
    const t: Record<string, string> = {
      M31: 'Now find the Andromeda Galaxy, two and a half million light years away: the most distant thing you can see with your own eyes. Binoculars help.',
      M45: 'Now find the Pleiades, a young cluster of blue stars. Binoculars show dozens of them.',
      M42: 'Now find the Orion Nebula, a glowing cloud where new stars are being born, below the three stars of Orion’s Belt.',
      M13: 'Now find the Great Globular Cluster in Hercules: hundreds of thousands of ancient stars in one ball. Binoculars help.',
      M44: 'Now find the Beehive Cluster, a lovely swarm of stars in binoculars.',
      M7: 'Now find Ptolemy’s Cluster, low in the south, known since ancient times.',
    };
    return t[id] ? { key: `dso-${id.toLowerCase()}`, text: t[id] } : null;
  },
  satellite(kind: 'iss' | 'tiangong' | 'hubble' | 'other'): Clip {
    const t = {
      iss: 'Later tonight the International Space Station passes over. It glides across the sky for a few minutes, like a bright, steady, moving star. The time and direction are on your screen.',
      tiangong: 'Later tonight China’s Tiangong space station passes over, like a steady, moving star. The time and direction are on your screen.',
      hubble: 'Later tonight the Hubble Space Telescope passes over, a faint, steady moving star. The time and direction are on your screen.',
      other: 'Later tonight a bright satellite passes over, like a steady, moving star. The time and direction are on your screen.',
    };
    return { key: `sat-${kind}`, text: t[kind] };
  },
  shower: (code: string, name: string): Clip => ({ key: `shower-${code.toLowerCase()}`, text: `The ${speakable(name)} are active tonight.` }),
  showerRate(rate: number): Clip {
    if (rate < 1) return { key: 'rate-low', text: 'Rates are low from here, but any shooting star you see may belong to them.' };
    if (rate < 6) return { key: 'rate-few', text: 'From here you might see a few an hour.' };
    if (rate < 15) return { key: 'rate-ten', text: 'From here you might see around ten an hour.' };
    return { key: 'rate-many', text: 'From here you might see twenty or more an hour.' };
  },
  showerRadiant: (): Clip => ({ key: 'shower-radiant', text: 'They seem to fly out from this point, but they can appear anywhere in the sky.' }),
};

// ---------- Names, for "what am I pointing at?" and "how do you say it?" ----------
const SPOKEN_DSO: Record<number, string> = {
  42: 'Orion Nebula', 44: 'Beehive Cluster', 45: 'Pleiades', 87: 'Virgo A galaxy', 99: 'Coma Pinwheel galaxy',
};
const withThe = (n: string) => (/'s\b/.test(n) ? n : `the ${n}`);

/**
 * Keys for the spoken facts on each card ("Listen"): two to four checked sentences about the thing itself.
 * Their words are kept, with their sources, in data-src/facts/facts.json.
 */
export const factKey = {
  body: (id: string) => `fact-body-${slug(id)}`,
  star: (name: string) => `fact-star-${slug(name)}`,
  constellation: (id: string) => `fact-con-${id.toLowerCase()}`,
  messier: (m: number) => `fact-m${m}`,
  satellite: (kind: 'iss' | 'tiangong' | 'hubble' | 'starlink' | 'other') => `fact-sat-${kind}`,
  shower: (code: string) => `fact-shower-${code.toLowerCase()}`,
  /** Arabic on: after a star's facts, its Arabic name (for the bright stars that have that line). */
  starArabic: (name: string) => `star-${slug(name)}-ar`,
};

export const nameClip = {
  body(id: string): Clip {
    const t: Record<string, string> = { Sun: 'The Sun. Never look straight at it.', Moon: 'The Moon.' };
    return { key: `name-body-${slug(id)}`, text: t[id] ?? `${id}, a planet.` };
  },
  star: (name: string, conName: string): Clip => ({ key: `name-star-${slug(name)}`, text: `${name}, a star in ${conName}.` }),
  constellation: (id: string, name: string, meaning: string): Clip => ({ key: `name-con-${slug(id)}`, text: meaning ? `${name}, the ${meaning}.` : `${name}, a constellation.` }),
  messier(m: number, name: string | undefined, type: string, conName: string): Clip {
    const n = SPOKEN_DSO[m] ?? name;
    return { key: `name-m${m}`, text: n ? `Messier ${m}, ${withThe(n)}.` : `Messier ${m}, ${/^[aeiou]/i.test(type) ? 'an' : 'a'} ${type.toLowerCase()} in ${conName}.` };
  },
  satellite(kind: 'iss' | 'tiangong' | 'hubble' | 'starlink' | 'other'): Clip {
    const t = { iss: 'The International Space Station.', tiangong: 'The Tiangong space station.', hubble: 'The Hubble Space Telescope.', starlink: 'A Starlink satellite.', other: 'A satellite.' };
    return { key: `name-sat-${kind}`, text: t[kind] };
  },
  shower: (code: string, name: string): Clip => ({ key: `name-shower-${code.toLowerCase()}`, text: `Where the ${speakable(name)} seem to come from.` }),
};
